import {
  Directive,
  Input,
  TemplateRef,
  ViewContainerRef,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { PermissionService } from '../../core/services/permission.service';

type PermissionMode = 'any' | 'all' | 'none';

@Directive({
  selector: '[appHasPermission]'
})
export class HasPermissionDirective implements OnInit, OnDestroy {
  private requiredPermissions: string[] = [];
  private mode: PermissionMode = 'any';
  private currentPermissions: string[] = [];
  private sub?: Subscription;
  private isVisible = false;

  @Input() set appHasPermission(value: string | string[] | null) {
    this.requiredPermissions = value == null ? [] : (Array.isArray(value) ? value : [value]);
    this.updateView();
  }

  @Input() set appHasPermissionMode(value: PermissionMode | string | null) {
    if (value === 'any' || value === 'all' || value === 'none') {
      this.mode = value;
    }
    this.updateView();
  }

  // Legacy alias support in case template uses hasMode
  @Input() set hasMode(value: PermissionMode | string | null) {
    if (value === 'any' || value === 'all' || value === 'none') {
      this.mode = value;
    }
    this.updateView();
  }

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private permService: PermissionService
  ) {}

  ngOnInit(): void {
    this.sub = this.permService.getPermissions$().subscribe(perms => {
      this.currentPermissions = perms || [];
      this.updateView();
    });

    this.currentPermissions = this.permService.getCurrentPermissions();
    this.updateView();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private updateView() {
    const allowed = this.checkPermission();

    if (allowed && !this.isVisible) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.isVisible = true;
    } else if (!allowed && this.isVisible) {
      this.viewContainer.clear();
      this.isVisible = false;
    }
  }

  private checkPermission(): boolean {
    if (!this.requiredPermissions || this.requiredPermissions.length === 0) return true;

    switch (this.mode) {
      case 'any':
        return this.permServiceHasAny();
      case 'all':
        return this.permServiceHasAll();
      case 'none':
        return this.permServiceHasNone();
      default:
        return false;
    }
  }

  private permServiceHasAny() {
    return this.requiredPermissions.some(p => this.currentPermissions.includes(p));
  }
  private permServiceHasAll() {
    return this.requiredPermissions.every(p => this.currentPermissions.includes(p));
  }
  private permServiceHasNone() {
    return !this.requiredPermissions.some(p => this.currentPermissions.includes(p));
  }
}
